import { Alert, Button, Flex, Input, Tag, Typography } from 'antd';
import {
  IconArrowLeft,
  IconBriefcase,
  IconShield,
  IconShieldLock,
  IconUserEdit,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import type { RoleDetailDto } from '~/app/services/authorization/role.service';
import {
  isRolePermissionsEditable,
  resolveSystemRoleDescription,
  resolveSystemRoleLabel,
} from '~/app/landing/admin/roles/utils';
import { useTimeAgo } from '~/app/hooks/useTimeAgo';

const { Paragraph, Text, Title } = Typography;

type RoleDetailHeroProps = {
  role: RoleDetailDto;
  useSystemRoleApi: boolean;
  assignedPermissionCount: number;
  totalPermissionCount: number;
  userRoleName: string;
  isRoleEditable: boolean;
  canAssignPermissions: boolean;
  canEditName: boolean;
  draftName: string;
  isNameDirty: boolean;
  isSavingName: boolean;
  onNameChange: (value: string) => void;
  onDiscardName: () => void;
  onSaveName: () => void;
  onBack: () => void;
};

export function RoleDetailHero({
  role,
  useSystemRoleApi,
  assignedPermissionCount,
  totalPermissionCount,
  userRoleName,
  isRoleEditable,
  canAssignPermissions,
  canEditName,
  draftName,
  isNameDirty,
  isSavingName,
  onNameChange,
  onDiscardName,
  onSaveName,
  onBack,
}: RoleDetailHeroProps) {
  const { t } = useTranslation('authorization');
  const { timeAgo } = useTimeAgo();
  const label = resolveSystemRoleLabel(role.name, t);
  const description = resolveSystemRoleDescription(role.name, t);
  const isSuperAdminReadOnly =
    role.isSystemRole && role.name === SystemRoleName.SUPER_ADMIN;
  const isEditableSystemRole =
    role.isSystemRole &&
    isRolePermissionsEditable(role, { roleName: userRoleName });

  const Icon = role.isSystemRole
    ? isSuperAdminReadOnly
      ? IconShieldLock
      : IconShield
    : IconBriefcase;

  const modeLabel = !canAssignPermissions
    ? t('detail.viewOnlyMode')
    : isRoleEditable
      ? t('detail.editMode')
      : t('detail.viewOnlyMode');

  const showAuditInfo = !role.isSystemRole && !useSystemRoleApi;
  const unknownUserLabel = t('detail.unknownUser');

  const createdByFirstName = role.createdBy?.firstName || unknownUserLabel;
  const createdByLastName = role.createdBy?.lastName ?? '';
  const updatedByFirstName = role.updatedBy?.firstName || unknownUserLabel;
  const updatedByLastName = role.updatedBy?.lastName ?? '';

  return (
    <Flex vertical gap={16} className="authorization-role-detail-hero-wrap">
      <Button
        type="text"
        className="authorization-back-link"
        icon={<IconArrowLeft size={16} />}
        onClick={onBack}
      >
        {t('detail.back')}
      </Button>

      <div className="authorization-role-detail-hero">
        <Flex gap={20} align="flex-start" wrap>
          <span
            className={`authorization-role-detail-hero-icon ${
              role.isSystemRole
                ? 'authorization-role-detail-hero-icon--system'
                : 'authorization-role-detail-hero-icon--custom'
            }`}
            aria-hidden
          >
            <Icon size={28} stroke={1.6} />
          </span>

          <Flex
            vertical
            gap={10}
            className="authorization-role-detail-hero-content"
          >
            <Flex align="center" gap={8} wrap>
              {canEditName ? (
                <Flex
                  align="center"
                  gap={8}
                  wrap
                  className="authorization-role-detail-hero-name-row"
                >
                  <Input
                    value={draftName}
                    maxLength={100}
                    placeholder={t('detail.namePlaceholder')}
                    aria-label={t('detail.nameLabel')}
                    disabled={isSavingName}
                    className="authorization-role-detail-hero-name-input"
                    onChange={(event) => onNameChange(event.target.value)}
                  />
                  {isNameDirty && (
                    <>
                      <Button
                        size="small"
                        disabled={isSavingName}
                        onClick={onDiscardName}
                      >
                        {t('detail.discardName')}
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        loading={isSavingName}
                        onClick={onSaveName}
                      >
                        {t('detail.saveName')}
                      </Button>
                    </>
                  )}
                </Flex>
              ) : (
                <Title
                  level={2}
                  className="authorization-role-detail-hero-title"
                >
                  {label}
                </Title>
              )}
              {role.isSystemRole ? (
                <Tag className="authorization-role-detail-hero-badge authorization-role-detail-hero-badge--system">
                  {t('rolesTable.systemRole')}
                </Tag>
              ) : (
                <Tag className="authorization-role-detail-hero-badge authorization-role-detail-hero-badge--custom">
                  {t('detail.customRoleBadge')}
                </Tag>
              )}
              <Tag
                className={`authorization-role-detail-hero-badge ${
                  isRoleEditable && canAssignPermissions
                    ? 'authorization-role-detail-hero-badge--editable'
                    : 'authorization-role-detail-hero-badge--muted'
                }`}
              >
                {modeLabel}
              </Tag>
            </Flex>

            {description && (
              <Paragraph className="authorization-role-detail-hero-description">
                {description}
              </Paragraph>
            )}

            <Flex
              gap={12}
              wrap
              className="authorization-role-detail-hero-stats"
            >
              <div className="authorization-stat-pill">
                <Text className="authorization-stat-pill-value">
                  {assignedPermissionCount}
                  <span className="authorization-stat-pill-separator">/</span>
                  {totalPermissionCount}
                </Text>
                <Text className="authorization-stat-pill-label">
                  {t('detail.statsPermissions')}
                </Text>
              </div>

              {!useSystemRoleApi && (
                <div className="authorization-stat-pill">
                  <Flex align="center" gap={6}>
                    <IconUsers size={16} stroke={1.75} />
                    <Text className="authorization-stat-pill-value">
                      {role.usersCount}
                    </Text>
                  </Flex>
                  <Text className="authorization-stat-pill-label">
                    {t('detail.statsUsers')}
                  </Text>
                </div>
              )}

              {isEditableSystemRole && canAssignPermissions && (
                <div className="authorization-stat-pill authorization-stat-pill--warning">
                  <Text className="authorization-stat-pill-label">
                    {t('detail.platformRoleHint')}
                  </Text>
                </div>
              )}
            </Flex>

            {showAuditInfo && (
              <Flex
                vertical
                gap={6}
                className="authorization-role-detail-audit"
              >
                <Text className="authorization-role-detail-audit-line">
                  <IconUserEdit size={15} stroke={1.75} aria-hidden />
                  {t('detail.createdBy', {
                    firstName: createdByFirstName,
                    lastName: createdByLastName,
                    date: timeAgo(role.createdAt),
                  })}
                </Text>
                <Text className="authorization-role-detail-audit-line">
                  <IconUserEdit size={15} stroke={1.75} aria-hidden />
                  {t('detail.updatedBy', {
                    firstName: updatedByFirstName,
                    lastName: updatedByLastName,
                    date: timeAgo(role.updatedAt),
                  })}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </div>

      {isSuperAdminReadOnly && (
        <Alert
          type="info"
          showIcon
          className="authorization-role-detail-alert"
          message={t('superAdminRoleReadOnly.banner')}
        />
      )}
    </Flex>
  );
}
